import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Button } from '@epoch-ui/components/Button';
import { InputField, DateTimePickerField } from '@epoch-ui/components/Form';
import {
  SelectField,
  SelectFieldContent,
  SelectFieldItem
} from '@epoch-ui/components/Form/fields/SelectField/SelectField';
import { SCHEDULE_UNIT } from 'constants/enums';
import dayjs from 'utils/extendedDayJS';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { FEEDBACK_SCHEDULE_TYPE, FEEDBACK_SETTINGS_DEFAULTS } from './feedbackSettingsSchema';

interface FeedbackScheduleSelectorProps {
  disabled?: boolean;
}

export const FeedbackScheduleSelector: React.FC<FeedbackScheduleSelectorProps> = ({ disabled }) => {
  const event = useEditableEvent();
  const { control, reset } = useFormContext();

  const quantity = useWatch({ control, name: 'feedbackSchedule.quantity' });
  const unit = useWatch({ control, name: 'feedbackSchedule.unit' });
  const beforeEventEnds = useWatch({ control, name: 'feedbackSchedule.beforeEventEnds' });

  const getCalendarDateDisabled = useCallback(
    (date: Date): boolean => {
      const targetTz = event.timezoneCode ?? dayjs.tz.guess();
      const today = dayjs(dayjs.convertToStartOfDay(new Date(), targetTz));
      const wallClockDate = dayjs(date).format('YYYY-MM-DD');
      const newDateTime = dayjs.tz(`${wallClockDate} 00:00`, 'YYYY-MM-DD HH:mm', targetTz);
      return newDateTime.isBefore(today);
    },
    [event.timezoneCode]
  );

  const isRelativeTime =
    quantity !== undefined && unit !== undefined && beforeEventEnds !== undefined;

  const formattedEventDateTime = useMemo(() => {
    if (!event.datetimeScheduled || !event.datetimeEnd) {
      return null;
    }

    return dayjs.formatEventDateTime({
      datetimeScheduled: event.datetimeScheduled,
      datetimeEnd: event.datetimeEnd,
      allDay: event.allDay ?? false,
      eventTimezone: event.timezoneCode,
      displayedTimezone: event.timezoneCode
    });
  }, [event.datetimeScheduled, event.datetimeEnd, event.allDay, event.timezoneCode]);

  const handleToggle = useCallback((): void => {
    const { feedbackSchedule, timezoneCode } = event;

    if (isRelativeTime) {
      const sendAt = feedbackSchedule?.sendAt
        ? convertSendAtToFormValue(feedbackSchedule?.sendAt, timezoneCode)
        : undefined;

      reset((currentValues) => ({
        ...currentValues,
        feedbackSchedule: {
          ...currentValues.feedbackSchedule,
          quantity: undefined,
          unit: undefined,
          beforeEventEnds: undefined,
          sendAt
        }
      }));
    } else {
      const defaults = FEEDBACK_SETTINGS_DEFAULTS.feedbackSchedule;

      reset((currentValues) => ({
        ...currentValues,
        feedbackSchedule: {
          ...currentValues.feedbackSchedule,
          sendAt: undefined,
          quantity: feedbackSchedule?.quantity ?? defaults.quantity,
          unit: feedbackSchedule?.unit ?? defaults.unit,
          beforeEventEnds:
            feedbackSchedule?.beforeEventEnds ?? defaults.beforeEventEnds
              ? FEEDBACK_SCHEDULE_TYPE.BEFORE_EVENT_ENDS
              : FEEDBACK_SCHEDULE_TYPE.AFTER_EVENT_ENDS
        }
      }));
    }
  }, [isRelativeTime, event.feedbackSchedule, event.timezoneCode, reset]);

  return (
    <div>
      {/* Relative date-time selector */}
      {isRelativeTime && (
        <div>
          <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3">
            <InputField
              type="number"
              min={1}
              className="tw-mt-0"
              inputClassName="tw-w-20 tw-mt-0"
              name="feedbackSchedule.quantity"
              disabled={disabled}
            />

            <SelectField
              className="tw-mt-0 tw-flex-grow"
              name="feedbackSchedule.unit"
              disabled={disabled}
            >
              <SelectFieldContent>
                <SelectFieldItem value={SCHEDULE_UNIT.MINUTES}>minutes</SelectFieldItem>
                <SelectFieldItem value={SCHEDULE_UNIT.HOURS}>hours</SelectFieldItem>
                <SelectFieldItem value={SCHEDULE_UNIT.DAYS}>days</SelectFieldItem>
              </SelectFieldContent>
            </SelectField>
          </div>

          <SelectField
            name="feedbackSchedule.beforeEventEnds"
            className="tw-mt-2"
            disabled={disabled}
          >
            <SelectFieldContent>
              <SelectFieldItem value={FEEDBACK_SCHEDULE_TYPE.BEFORE_EVENT_ENDS}>
                before the event ends
              </SelectFieldItem>
              <SelectFieldItem value={FEEDBACK_SCHEDULE_TYPE.AFTER_EVENT_ENDS}>
                after the event ends
              </SelectFieldItem>
            </SelectFieldContent>
          </SelectField>
        </div>
      )}

      {/* Absolute date-time selector */}
      {!isRelativeTime && (
        <div>
          <div className="tw-mt-1 tw-flex tw-flex-col tw-text-xs tw-leading-4 tw-text-primary-grey">
            <span>Event is scheduled for {formattedEventDateTime}.</span>

            <span>
              You&apos;re scheduling in ({dayjs.getGMTOffset(event.timezoneCode)}) -{' '}
              {dayjs.getTimezoneName(event.timezoneCode)}.
            </span>
          </div>

          <div className="tw-mt-3">
            <DateTimePickerField name="feedbackSchedule.sendAt" disabled={disabled}>
              <DateTimePickerField.DateInput
                placeholder="Date"
                disabled={getCalendarDateDisabled}
              />
              <DateTimePickerField.Time interval={15} placeholder="Time" />
            </DateTimePickerField>
          </div>
        </div>
      )}

      <Button
        variant="link"
        className="tw-mt-3 tw-h-5 tw-p-0 tw-font-normal"
        type="button"
        onClick={handleToggle}
        disabled={disabled}
      >
        {isRelativeTime ? 'Use date and time' : 'Use relative time'}
      </Button>
    </div>
  );
};
